<template>
    <Form class="form p-4 grid flex flex-column justify-content-center align-items-center">
        <div class="py-4">
            <span class="H6-Medium">Verification Request</span>
        </div>
        <div class="flex flex-column justify-content-between ">
            <div class="flex flex-wrap justify-content-between  md:gap-8 py-2">
                <label class="Label text-left">Initial review</label>
                <template v-if="initialReview || tutorProfileDetails.initialReviewByAdmins">
                    <i class="pi pi-check-circle text-green-500 text-xl align-items-center"></i>
                </template>
                <template v-else>
                    <Button label="Complete" class="accent-button" @click="onInitialReviewClick" />
                </template>
            </div>
            <div class="flex flex-wrap justify-content-between  md:gap-8 py-2">
                <label class="Label">Document verification</label>
                <template v-if="documentVerification || tutorProfileDetails.documentVerificationByAdmins">
                    <i class="pi pi-check-circle text-green-500 text-xl align-items-center"></i>
                </template>
                <template v-else>
                    <Button label="Complete" class="accent-button" @click="documentVerificationCheck" />
                </template>
            </div>
            <div class="flex flex-wrap justify-content-between md:gap-8 py-2">
                <label class="Label">Background check</label>
                <template v-if="backgroundCheckStatus || tutorProfileDetails.backgroundCheckByAdmins">
                    <i class="pi pi-check-circle text-green-500 text-xl align-items-center"></i>
                </template>
                <template v-else>
                    <Button label="Complete" class="accent-button" @click="backgroundCheck" />
                </template>
            </div>
            <div class="flex flex-wrap justify-content-between  md:gap-8 py-2">
                <label class="Label">Final review</label>
                <template v-if="finalStatus || tutorProfileDetails.finalReviewByAdmins">
                    <i class="pi pi-check-circle text-green-500 text-xl align-items-center"></i>
                </template>
                <template v-else>
                    <Button label="Complete" class="accent-button" @click="finalStatusCheck" />
                </template>
            </div>
            <div class="flex flex-wrap justify-content-center gap-4 mt-6">
                <Button label="Approve as a Tutor" class="accent-button" :disabled="btnDisable()" @click="approve"
                    :loading="loading" />
                <Button label="Reject Application" class="accent-button-danger" severity="danger" variant="outlined"
                    @click="reject" />
            </div>
        </div>
        <Divider class="w-full py-2" />
        <div class="py-4">
            <span class="H6-Medium">Personal Information</span>
        </div>
        <div class="md:col-10">
            <div class="flex justify-content-center mb-4">
                <div class="flex flex-column align-items-center">
                    <div class="profile-image-container mb-2">
                        <img
                            :src="signedPhotoUrl || blankProfile"
                            alt="Tutor Photo"
                            style="width: 120px; height: 120px; border-radius: 8px; object-fit: cover; border: 1px solid #ddd;"
                        />
                    </div>
                    <label class="Label text-center" style="cursor: pointer;" @click="tutorProfileDetails?.professionalPhotoURL ? downloadByUrl(tutorProfileDetails?.professionalPhotoURL) : null">Profile Photo</label>
                </div>
            </div>
            <div class="info-grid">
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">First Name</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.firstName || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Last Name</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.lastName || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Email</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.email || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Mobile Number</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.mobileNumber || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">NIC</span>
                    <span class="Label info-value">
                        {{ tutorProfileDetails?.NIC || 'N/A' }}
                        <a v-if="tutorProfileDetails?.NICURL" @click="downloadByUrl(tutorProfileDetails.NICURL)" style="color: blue; cursor: pointer;"> Download</a>
                    </span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Date of Birth</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.dateOfBirth ? tutorProfileDetails.dateOfBirth.split('T')[0] : 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Gender</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.gender || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="Paragraph-Medium info-label">Address</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.address || 'N/A' }}</span>
                </div>
                <div class="info-row info-row--full">
                    <span class="Paragraph-Medium info-label">Bio</span>
                    <span class="Label info-value">{{ tutorProfileDetails?.tutorBio || 'N/A' }}</span>
                </div>
            </div>
        </div>
        <Divider class="w-full py-2" />
        <div class="py-4">
            <span class="H6-Medium">Teaching Informations</span>
        </div>
        <div class="md:col-10">
            <div class="col-12">
                <div class="flex flex-column gap-3 pt-2">
                    <label for="grades" class="Paragraph-Medium align-self-start">Grade and subjects</label>
                    <div v-if="!tutorProfileDetails?.teachingLevels || tutorProfileDetails.teachingLevels.length === 0">
                        <span class="Label">No grades or subjects listed.</span>
                    </div>
                    <div v-else class="teaching-container flex flex-column gap-4">
                        <div v-for="(group, gIndex) in groupedTeachingLevels" :key="gIndex" class="teaching-grade-block">
                            <div class="grade-header-row mb-1 border-bottom-1 border-gray-100 pb-1">
                                <span class="Label font-semibold">{{ cleanupLables(group.grade) }}</span>
                                <span class="text-xs text-500 font-medium text-right">Individual Class</span>
                                <span class="text-xs text-500 font-medium text-right">Group Class</span>
                            </div>
                            <div class="flex flex-column pl-2 md:pl-4 mt-1">
                                <div v-for="entry in group.entries" :key="entry.subject" class="subject-row">
                                    <span class="Label">{{ cleanupLables(entry.subject) }}</span>
                                    <span class="Label block w-full text-right">
                                        {{ entry.individualClass?.available ? formatPrice(entry.individualClass.charge) : '—' }}
                                    </span>
                                    <span class="Label block w-full text-right">
                                        {{ entry.groupClass?.available ? formatPrice(entry.groupClass.charge) : '—' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-wrap justify-content-between">
                <div class=" flex flex-column gap-3 pt-4 ">
                    <label for="occupation" class="Paragraph-Medium align-self-start">I'm a - <span class="Label">{{cleanupLables(tutorProfileDetails?.tutorType) || "N/A" }}</span></label>
                </div>
                <div class="flex flex-column gap-3 pt-4">
                    <div class="flex flex-wrap gap-1">
                        <label for="occupation" class="Paragraph-Medium align-self-start">I teach in - </label>
                        <div class="flex flex-column gap-2">
                            <span v-for="language in tutorProfileDetails?.teachingLanguage" :key="language"
                                class="Label">{{cleanupLables(language) || "N/A" }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="flex flex-column gap-3 pt-4">
                    <div class="flex flex-wrap gap-1">
                        <label for="occupation" class="Paragraph-Medium align-self-start">I'm available in -</label>
                        <div class="flex flex-column gap-2">
                            <span v-for="availability in tutorProfileDetails?.teachingAvailability" :key="availability"
                                class="Label">
                                {{ cleanupLables(availability) || "N/A"}}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-wrap justify-content-between py-4">
                <div class=" flex flex-column gap-3 pt-4 ">
                    <label for="occupation" class="Paragraph-Medium align-self-start">I do classes in - <span
                            class="Label">{{ formattedClassMode }}</span></label>
                </div>
                <div class=" flex flex-column gap-3 pt-4 ">
                    <label for="occupation" class="Paragraph-Medium align-self-start">Class location - <span
                            class="Label">{{ getLocationLabel(tutorProfileDetails?.classLocation) }}</span></label>
                </div>
            </div>
        </div>
        <Divider class="w-full py-2" />
        <div class="py-4">
            <span class="H6-Medium">Teaching Experiences & Qualifications</span>
        </div>
        <div class="md:col-10">
            <div class="col-12">
                <label class="Paragraph-Medium align-self-start flex ">Previous Experiences</label>
                <div class="grid mt-2 gap-1 flex justify-content-center">
                    <div v-for="(exp, index) in tutorProfileDetails?.experience" :key="index" class="col-12 md:col-6 ">
                        <div class="grid">
                            <div class="col-5 flex align-items-center">
                                <span class="Label">{{ exp.institution || "N/A"}}</span>
                            </div>
                            <div class="col-7 flex align-items-center">
                                <span class="Label">{{ exp.yearsOfExperience || "N/A"}} Years</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3">
                <label class="Paragraph-Medium align-self-start flex">Highest Education</label>
                <div class="grid mt-2 gap-1 flex justify-content-center">
                    <div v-for="(edu, index) in tutorProfileDetails?.highestEducation" :key="'edu-' + index"
                        class="col-12 md:col-6">
                        <div class="grid">
                            <div class="col-6 flex align-items-center">
                                <span class="Label">{{ edu.education || "N/A" }}</span>
                            </div>
                            <div class="col-6 flex align-items-center" v-if="edu.certificationURL">
                                <Button label="Download" class="teal-button-transparent"
                                    @click="downloadByUrl(edu.certificationURL)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3">
                <label class="Paragraph-Medium align-self-start flex ">Teaching Qualifications</label>
                <div class="grid mt-2 gap-1 flex justify-content-center">
                    <div v-for="(cert, index) in tutorProfileDetails?.teachingQualifications" :key="index"
                        class="col-12 md:col-6">
                        <div class="grid">
                            <div class="col-6 flex align-items-center">
                                <span class="Label">{{ cert.qualification|| "N/A" }}</span>
                            </div>
                            <div class="col-6 flex align-items-center" v-if="cert.qualification">
                                <Button label="Download" class="teal-button-transparent"
                                    @click="downloadByUrl(cert.certificateURL)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3">
                <label class="Paragraph-Medium align-self-start flex ">Notable Achievements & Certifications</label>
                <div class="grid mt-2 gap-1 flex justify-content-center">
                    <div v-for="(cert, index) in tutorProfileDetails?.notableAchievements" :key="index"
                        class="col-12 md:col-6">
                        <div class="grid pt-2">
                            <div class="col-6 flex align-items-center">
                                <span class="Label">{{ cert.achievement || "N/A" }}</span>
                            </div>
                            <div class="col-6 flex align-items-center" v-if="cert.uploadURL">
                                <Button label="Download" class="teal-button-transparent"
                                    @click="downloadByUrl(cert.uploadURL)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Divider class="w-full py-2" />
        <div class="py-4">
            <span class="H6-Medium">Identity Documents</span>
        </div>
        <div class="md:col-10">
            <div class="col-12">
                <div class="flex flex-column gap-3">
                    <div class="flex align-items-center justify-content-between py-2">
                        <span class="Paragraph-Medium">NIC Number</span>
                        <span class="Label">{{ tutorProfileDetails?.NIC || "N/A" }}</span>
                    </div>
                    <div class="flex align-items-center justify-content-between py-2">
                        <span class="Paragraph-Medium">NIC Photo</span>
                        <Button v-if="tutorProfileDetails?.NICURL" label="Download" class="teal-button-transparent"
                            @click="downloadByUrl(tutorProfileDetails.NICURL)" />
                        <span v-else class="Label">Not uploaded</span>
                    </div>
                    <div class="flex align-items-center justify-content-between py-2">
                        <span class="Paragraph-Medium">Professional Photo</span>
                        <Button v-if="tutorProfileDetails?.professionalPhotoURL" label="Download" class="teal-button-transparent"
                            @click="downloadByUrl(tutorProfileDetails.professionalPhotoURL)" />
                        <span v-else class="Label">Not uploaded</span>
                    </div>
                    <div class="flex align-items-center justify-content-between py-2">
                        <span class="Paragraph-Medium">Driving License</span>
                        <Button v-if="tutorProfileDetails?.drivingLicenseURL" label="Download" class="teal-button-transparent"
                            @click="downloadByUrl(tutorProfileDetails.drivingLicenseURL)" />
                        <span v-else class="Label">Not uploaded</span>
                    </div>
                </div>
            </div>
        </div>
    </Form>
</template>
<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import Divider from "primevue/divider";
import { generateSignedUrl } from "@/api/storage";
import { cleanupLables } from "@/utils/cleanups";
import blankProfile from '@/assets/blank-profile.png';
const props = defineProps({
    tutorProfileDetails: null,
    loading: {
        type: Boolean,
        default: false
    }
})
const emit = defineEmits([
    "update-initialReview",
    "update-document",
    "update-background",
    "update-finalstatus",
    "approve-tutor",
    "reject-tutor"
]);

const initialReview = ref(false);
const documentVerification = ref(false);
const backgroundCheckStatus = ref(false);
const finalStatus = ref(false);
const signedPhotoUrl = ref("");

const handleEmit = (eventName, logLabel, statusRef) => {
    try {
        emit(eventName);
        statusRef.value = true;
    } catch (error) {
        console.error(`Failed to complete ${logLabel}:`, error);
    }
};

watch(
  () => props.tutorProfileDetails?.professionalPhotoURL, // Removed .value
  async (newUrl) => {
    if (newUrl) {
      try {
        // If your generateSignedUrl returns a string, this is fine.
        // If it returns an object, use signedPhotoUrl.value = result.url;
        const result = await generateSignedUrl(newUrl);
        signedPhotoUrl.value = result;
      } catch (error) {
        console.error("Error signing URL:", error);
        signedPhotoUrl.value = null;
      }
    }
  },
  { immediate: true, deep: true } // Added deep: true just in case the object reference updates
);

const onInitialReviewClick = () =>
    handleEmit("update-initialReview", "initial review", initialReview);

const documentVerificationCheck = () =>
    handleEmit("update-document", "document verification", documentVerification);

const backgroundCheck = () =>
    handleEmit("update-background", "background check", backgroundCheckStatus);

const finalStatusCheck = () =>
    handleEmit("update-finalstatus", "final status", finalStatus);

const btnDisable = () => {
    const details = props.tutorProfileDetails;
    return !(
        details.initialReviewByAdmins || initialReview.value &&
        details.documentVerificationByAdmins || documentVerification.value &&
        details.backgroundCheckByAdmins || backgroundCheckStatus.value &&
        details.finalReviewByAdmins || finalStatus.value
    );
}
const approve = () => {
    emit("approve-tutor");
};

const reject = () => {
    emit("reject-tutor");
};

const getLocationLabel = (key) => {
    const locations = [
        { value: "Tutor's place", key: "tutors_place" },
        { value: "Student's place", key: "students_place" },
        { value: "Tutor's & Student's Place", key: "both" },
    ];
    const found = locations.find(l => l.key === key);
    return found ? found.value : (key || "N/A");
};

const groupedTeachingLevels = computed(() => {
    if (!props.tutorProfileDetails?.teachingLevels) return [];
    const map = {};
    props.tutorProfileDetails.teachingLevels.forEach(entry => {
        if (!map[entry.grade]) {
            map[entry.grade] = { grade: entry.grade, entries: [] };
        }
        map[entry.grade].entries.push(entry);
    });
    return Object.values(map);
});

const formatPrice = (amount) => {
    if (amount == null || amount <= 0) return '—';
    return `Rs. ${new Intl.NumberFormat('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
};

const formattedClassMode = computed(() => {
    const mode = props.tutorProfileDetails?.classMode;
    if (mode === 'both') return 'Physical & Online';
    if (!mode || typeof mode !== 'string') return 'N/A';
    return mode.charAt(0).toUpperCase() + mode.slice(1);
});

const downloadByUrl = async (url) => {
    const res = await generateSignedUrl(url);
    const signedUrl = res;

    const link = document.createElement('a');
    link.href = signedUrl;
    link.download = url || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
</script>
<style scoped>
.form {
    width: 100%;
    background-color: white;
}

.accent-button-danger {
    padding: 8px 16px 8px 16px;
    height: 34px;
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    width: 100%;
}

.info-row {
    display: grid;
    grid-template-columns: 10rem 1fr;
    align-items: baseline;
    padding: 0.5rem 1rem;
}

.info-row--full {
    grid-column: 1 / -1;
}

.info-label {
    color: var(--gray-600, #6b7280);
}

.info-value {
    word-break: break-word;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }

    .info-grid {
        grid-template-columns: 1fr;
    }

    .info-row--full {
        grid-column: 1;
    }

    .grade-header-row,
    .subject-row {
        grid-template-columns: 1fr 7rem 7rem;
        gap: 0.25rem;
    }
}

.teaching-container {
    width: 100%;
}

.teaching-grade-block {
    width: 100%;
    margin-bottom: 1rem;
}

.grade-header-row,
.subject-row {
    display: grid;
    grid-template-columns: 1fr 9rem 9rem;
    gap: 0.5rem;
    align-items: center;
    padding: 0.2rem 0;
}

.subject-row {
    padding: 0.3rem 0;
}

.subject-row:last-child {
    border-bottom: none;
}
</style>